import React from 'react';
import './App.css';
import { PowerProvider } from './state/PowerContext';
import { usePowerData } from './hooks/usePowerData';
import TopBar from './components/TopBar';
// Legacy big cards — kept on disk for easy revert if we want them back.
// import StatusCard from './components/StatusCard';
// import NextDangerCard from './components/NextDangerCard';
import CompactHeader from './components/CompactHeader';
// Removed as redundant — kept on disk for easy revert.
// import Marquee from './components/Marquee';
// import TimesToWatch from './components/TimesToWatch';
import HourGrid from './components/HourGrid';
import CurtailmentPanel from './components/CurtailmentPanel';
import BillingGroupsPanel from './components/BillingGroupsPanel';
import HiddenPeakRisk from './components/HiddenPeakRisk';
import HistoricalPeaks from './components/HistoricalPeaks';
import AlertEngine from './components/AlertEngine';
import { usePower } from './state/PowerContext';
import { getHiddenPeakRisk, getAuthStatus } from './api';
import { useEffect, useState } from 'react';

function Dashboard() {
  const { loading, error, high, all, upcoming, dangerWindows, latestSheet } = usePowerData();
  const { series } = usePower();
  // Single shared fetch of weather-inferred hidden risk; fed to AlertEngine
  // and HiddenPeakRisk so they all agree and avoid duplicate calls.
  const [hiddenRisk, setHiddenRisk] = useState([]);
  useEffect(() => {
    let cancelled = false;
    getHiddenPeakRisk(series, 15)
      .then(d => { if (!cancelled) setHiddenRisk(d?.results || []); })
      .catch(() => { if (!cancelled) setHiddenRisk([]); });
    return () => { cancelled = true; };
  }, [series]);

  if (loading) {
    return <div className="loading">Loading period data…</div>;
  }
  if (error) {
    return (
      <div className="panel" style={{ borderColor: 'var(--danger)' }}>
        <h2 style={{ color: 'var(--danger)' }}>Error loading data</h2>
        <pre style={{ whiteSpace: 'pre-wrap', fontSize: 12 }}>{String(error?.message || error)}</pre>
      </div>
    );
  }

  const dangerCount = dangerWindows.filter(w => w.severity === 'danger').length;
  const warningCount = dangerWindows.filter(w => w.severity === 'warning').length;

  return (
    <>
      <AlertEngine upcoming={upcoming} high={high} hiddenRisk={hiddenRisk} />
      <CompactHeader high={high} dangerCount={dangerCount} warningCount={warningCount} latestSheet={latestSheet} records={all} />
      <HourGrid records={all} high={high} />
      <CurtailmentPanel />
      <BillingGroupsPanel />
      <HiddenPeakRisk periodHigh={high} records={all} hiddenRisk={hiddenRisk} />
      <HistoricalPeaks />
      <div className="meta-footer">
        Latest MISO sheet: {latestSheet || '—'} · {all.length} hours with data in the current period.
      </div>
    </>
  );
}

// LAN / localhost access (dev tooling, local dashboards) skips the login wall;
// anything reaching us through the public domain must have an auth session.
function isPrivateHost() {
  const h = window.location.hostname;
  return h === 'localhost' || h === '127.0.0.1'
    || /^10\./.test(h) || /^192\.168\./.test(h) || /^172\.(1[6-9]|2\d|3[01])\./.test(h);
}

function LoginGate({ children }) {
  const [auth, setAuth] = useState(null); // null = checking
  useEffect(() => {
    getAuthStatus().then(setAuth).catch(() => setAuth({ authenticated: false }));
  }, []);

  const exempt = isPrivateHost();
  if (!exempt && auth === null) {
    return <div className="loading" style={{ padding: 40 }}>Checking sign-in…</div>;
  }
  if (!exempt && !auth?.authenticated) {
    return (
      <div className="login-gate">
        <div className="login-card">
          <div className="brand" style={{ fontSize: 22, marginBottom: 6 }}>Power — Peak Watch</div>
          <p style={{ color: 'var(--text-dim)', fontSize: 13, margin: '0 0 18px' }}>
            Sign in with your Google account to continue.
          </p>
          <button className="primary" onClick={() => { window.location.href = '/api/auth/login'; }}>
            Sign in with Google
          </button>
        </div>
      </div>
    );
  }
  return children(auth);
}

export default function App() {
  return (
    <PowerProvider>
      <LoginGate>
        {(auth) => (
          <div className="app">
            <TopBar user={auth?.authenticated ? auth : null} />
            <Dashboard />
          </div>
        )}
      </LoginGate>
    </PowerProvider>
  );
}
